// Central dataset for form field label suggestions in multiple languages.
// - Easily extensible: add more languages by extending translations/aliases per entry
// - Match by language, query text, and optionally field type
// - Field types mirror the UI types but remain as string literals to avoid tight coupling
//
// Usage example:
//   getFieldLabelSuggestions({ lang: 'nl', query: 'voor', fieldType: 'text' })
//
// Note: 'any' in fieldTypes means suggestion applies to all field types
export const DEFAULT_LANGUAGES = ['en', 'nl', 'de'];
export const FIELD_LABEL_SUGGESTIONS = [
    // Person info
    {
        id: 'first-name',
        fieldTypes: ['text'],
        translations: { en: 'First Name', nl: 'Voornaam', de: 'Vorname' },
        aliases: {
            en: ['Given Name'],
            nl: ['Roepnaam'],
            de: ['Vorname(n)']
        },
        keywords: {
            en: ['name', 'first', 'given'],
            nl: ['naam', 'voor'],
            de: ['name', 'vor']
        }
    },
    {
        id: 'person-name',
        fieldTypes: ['text'],
        translations: { en: 'Name', nl: 'Naam', de: 'Name' },
        aliases: {
            en: ['Person Name', 'Individual Name'],
            nl: ['Persoonsnaam'],
            de: ['Personenname']
        },
        keywords: { en: ['name'], nl: ['naam'], de: ['name'] }
    },
    {
        id: 'employee-name',
        fieldTypes: ['text'],
        translations: { en: 'Employee Name', nl: 'Naam medewerker', de: 'Name des Mitarbeiters' },
        aliases: { en: ['Staff Name'], nl: ['Medewerker naam'], de: ['Mitarbeitername'] },
        keywords: { en: ['name', 'employee', 'staff'], nl: ['naam', 'medewerker'], de: ['name', 'mitarbeiter'] }
    },
    {
        id: 'attendee-name',
        fieldTypes: ['text'],
        translations: { en: 'Attendee Name', nl: 'Naam deelnemer', de: 'Name des Teilnehmers' },
        aliases: { en: ['Participant Name', 'Guest Name'], nl: ['Naam gast'], de: ['Name des Gastes'] }
    },
    {
        id: 'guardian-name',
        fieldTypes: ['text'],
        translations: { en: 'Guardian Name', nl: 'Naam voogd', de: 'Name des Vormunds' },
        aliases: { en: ['Caretaker Name'], nl: [], de: [] }
    },
    {
        id: 'father-name',
        fieldTypes: ['text'],
        translations: { en: 'Father’s Name', nl: 'Naam vader', de: 'Name des Vaters' }
    },
    {
        id: 'mother-name',
        fieldTypes: ['text'],
        translations: { en: 'Mother’s Name', nl: 'Naam moeder', de: 'Name der Mutter' }
    },
    {
        id: 'spouse-name',
        fieldTypes: ['text'],
        translations: { en: 'Spouse Name', nl: 'Naam partner', de: 'Name des Ehepartners' },
        aliases: { en: ['Partner Name'], nl: ['Partnernaam'], de: ['Partnername'] }
    },
    {
        id: 'contact-name',
        fieldTypes: ['text'],
        translations: { en: 'Contact Name', nl: 'Naam contactpersoon', de: 'Name Kontaktperson' },
        aliases: { en: ['POC Name'], nl: ['Contactnaam'], de: ['Kontaktname'] }
    },
    {
        id: 'child-name',
        fieldTypes: ['text'],
        translations: { en: 'Child Name', nl: 'Naam kind', de: 'Name des Kindes' }
    },
    {
        id: 'middle-name',
        fieldTypes: ['text'],
        translations: { en: 'Middle Name', nl: 'Tussenvoegsel', de: 'Zweiter Vorname' },
        aliases: { en: ['Middle Initial'], nl: [], de: [] },
        keywords: {
            en: ['name', 'middle', 'initial'],
            nl: ['tussen'],
            de: ['zweiter', 'mittel']
        }
    },
    {
        id: 'last-name',
        fieldTypes: ['text'],
        translations: { en: 'Last Name', nl: 'Achternaam', de: 'Nachname' },
        aliases: { en: ['Family Name', 'Surname'], nl: [], de: ['Familienname'] },
        keywords: {
            en: ['name', 'last', 'surname', 'family'],
            nl: ['naam', 'achter'],
            de: ['name', 'nach', 'familien']
        }
    },
    {
        id: 'full-name',
        fieldTypes: ['text'],
        translations: { en: 'Full Name', nl: 'Volledige naam', de: 'Vollständiger Name' },
        keywords: { en: ['name'], nl: ['naam'], de: ['name'] }
    },
    {
        id: 'email',
        fieldTypes: ['email'],
        translations: { en: 'Email', nl: 'E-mail', de: 'E-Mail' },
        aliases: { en: ['Email Address'], nl: ['Emailadres'], de: ['E-Mail-Adresse'] },
        keywords: { en: ['mail'], nl: ['mail'], de: ['mail'] }
    },
    {
        id: 'phone',
        fieldTypes: ['phone'],
        translations: { en: 'Phone', nl: 'Telefoon', de: 'Telefon' },
        aliases: {
            en: ['Telephone Number', 'Phone Number'],
            nl: ['Telefoonnummer'],
            de: ['Telefonnummer']
        },
        keywords: { en: ['tel', 'phone'], nl: ['tel', 'telefoon'], de: ['tel', 'telefon'] }
    },
    {
        id: 'mobile',
        fieldTypes: ['phone'],
        translations: { en: 'Mobile', nl: 'Mobiel', de: 'Handy' },
        aliases: {
            en: ['Mobile Number', 'Cell'],
            nl: ['Mobiel nummer', 'GSM'],
            de: ['Mobilnummer', 'Handynummer']
        },
        keywords: { en: ['cell', 'mobile'], nl: ['gsm', 'mobiel'], de: ['mobil', 'handy'] }
    },
    {
        id: 'date-of-birth',
        fieldTypes: ['date'],
        translations: { en: 'Date of Birth', nl: 'Geboortedatum', de: 'Geburtsdatum' },
        aliases: { en: ['DOB', 'Birthdate'], nl: [], de: [] },
        keywords: { en: ['dob', 'birth'], nl: ['geboorte'], de: ['geburts'] }
    },
    {
        id: 'gender',
        fieldTypes: ['dropdown', 'radio', 'choice', 'multi-select'],
        translations: { en: 'Gender', nl: 'Geslacht', de: 'Geschlecht' },
        keywords: { en: [], nl: [], de: [] }
    },
    {
        id: 'nationality',
        fieldTypes: ['dropdown', 'text'],
        translations: { en: 'Nationality', nl: 'Nationaliteit', de: 'Staatsangehörigkeit' }
    },
    {
        id: 'id-number',
        fieldTypes: ['text'],
        translations: { en: 'ID Number', nl: 'Identificatienummer', de: 'Ausweisnummer' }
    },
    {
        id: 'passport-number',
        fieldTypes: ['text'],
        translations: { en: 'Passport Number', nl: 'Paspoortnummer', de: 'Reisepassnummer' }
    },
    {
        id: 'employee-id',
        fieldTypes: ['text'],
        translations: { en: 'Employee ID', nl: 'Medewerkersnummer', de: 'Mitarbeiter-ID' },
        aliases: { en: ['Staff ID'], nl: [], de: ['Mitarbeiterausweisnummer'] }
    },
    {
        id: 'job-title',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Job Title', nl: 'Functietitel', de: 'Berufsbezeichnung' }
    },
    {
        id: 'department',
        fieldTypes: ['dropdown', 'text'],
        translations: { en: 'Department', nl: 'Afdeling', de: 'Abteilung' }
    },
    {
        id: 'company',
        fieldTypes: ['companyDropdown', 'dropdown', 'text'],
        translations: { en: 'Company', nl: 'Bedrijf', de: 'Unternehmen' }
    },
    {
        id: 'address-line-1',
        fieldTypes: ['text', 'multiline'],
        translations: { en: 'Address Line 1', nl: 'Adresregel 1', de: 'Adresszeile 1' },
        aliases: { en: ['Street Address'], nl: ['Straat en nummer'], de: ['Straße und Hausnummer'] }
    },
    {
        id: 'address-line-2',
        fieldTypes: ['text', 'multiline'],
        translations: { en: 'Address Line 2', nl: 'Adresregel 2', de: 'Adresszeile 2' }
    },
    {
        id: 'city',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'City', nl: 'Stad', de: 'Stadt' }
    },
    {
        id: 'state',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'State', nl: 'Provincie', de: 'Bundesland' },
        aliases: { en: ['Province', 'Region'], nl: ['Regio'], de: ['Region'] }
    },
    {
        id: 'postal-code',
        fieldTypes: ['text'],
        translations: { en: 'Postal Code', nl: 'Postcode', de: 'Postleitzahl' },
        aliases: { en: ['ZIP'], nl: [], de: ['PLZ'] }
    },
    {
        id: 'country',
        fieldTypes: ['dropdown', 'text'],
        translations: { en: 'Country', nl: 'Land', de: 'Land' }
    },
    {
        id: 'emergency-contact-name',
        fieldTypes: ['text'],
        translations: {
            en: 'Emergency Contact Name',
            nl: 'Naam noodgevalcontact',
            de: 'Name Notfallkontakt'
        }
    },
    {
        id: 'emergency-contact-phone',
        fieldTypes: ['phone', 'text'],
        translations: {
            en: 'Emergency Contact Phone',
            nl: 'Telefoon noodgevalcontact',
            de: 'Telefon Notfallkontakt'
        }
    },
    // Event registration & logistics
    {
        id: 'registration-type',
        fieldTypes: ['dropdown', 'radio', 'choice'],
        translations: { en: 'Registration Type', nl: 'Registratietype', de: 'Registrierungstyp' }
    },
    {
        id: 'number-of-adults',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Number of Adults', nl: 'Aantal volwassenen', de: 'Anzahl Erwachsene' }
    },
    {
        id: 'number-of-children',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Number of Children', nl: 'Aantal kinderen', de: 'Anzahl Kinder' }
    },
    {
        id: 'guests',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Guests', nl: 'Gasten', de: 'Gäste' }
    },
    {
        id: 'dietary-preferences',
        fieldTypes: ['multi-select', 'dropdown', 'checkbox-group'],
        translations: {
            en: 'Dietary Preferences',
            nl: 'Dieetvoorkeuren',
            de: 'Ernährungsvorlieben'
        },
        aliases: {
            en: ['Diet'],
            nl: ['Dieet'],
            de: ['Diät']
        }
    },
    {
        id: 'food-allergies',
        fieldTypes: ['multi-select', 'dropdown', 'checkbox-group'],
        translations: {
            en: 'Food Allergies',
            nl: 'Voedselallergieën',
            de: 'Lebensmittelallergien'
        }
    },
    {
        id: 'transportation-required',
        fieldTypes: ['toggle', 'radio', 'choice'],
        translations: {
            en: 'Transportation Required',
            nl: 'Vervoer nodig',
            de: 'Transport erforderlich'
        }
    },
    {
        id: 'pickup-location',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Pickup Location', nl: 'Ophaalplaats', de: 'Abholort' }
    },
    {
        id: 'dropoff-location',
        fieldTypes: ['text', 'dropdown'],
        translations: { en: 'Drop-off Location', nl: 'Afzetplaats', de: 'Absetzort' }
    },
    {
        id: 'arrival-date',
        fieldTypes: ['date'],
        translations: { en: 'Arrival Date', nl: 'Aankomstdatum', de: 'Ankunftsdatum' }
    },
    {
        id: 'arrival-time',
        fieldTypes: ['time'],
        translations: { en: 'Arrival Time', nl: 'Aankomsttijd', de: 'Ankunftszeit' }
    },
    {
        id: 'departure-date',
        fieldTypes: ['date'],
        translations: { en: 'Departure Date', nl: 'Vertrekdatum', de: 'Abreisedatum' }
    },
    {
        id: 'departure-time',
        fieldTypes: ['time'],
        translations: { en: 'Departure Time', nl: 'Vertrektijd', de: 'Abreisezeit' }
    },
    {
        id: 'flight-number',
        fieldTypes: ['text'],
        translations: { en: 'Flight Number', nl: 'Vluchtnummer', de: 'Flugnummer' }
    },
    {
        id: 'accommodation-required',
        fieldTypes: ['toggle', 'radio', 'choice'],
        translations: {
            en: 'Accommodation Required',
            nl: 'Accommodatie nodig',
            de: 'Unterkunft erforderlich'
        }
    },
    {
        id: 'hotel',
        fieldTypes: ['dropdown', 'text'],
        translations: { en: 'Hotel', nl: 'Hotel', de: 'Hotel' }
    },
    {
        id: 'check-in-date',
        fieldTypes: ['date'],
        translations: { en: 'Check-in Date', nl: 'Incheckdatum', de: 'Check-in-Datum' }
    },
    {
        id: 'check-out-date',
        fieldTypes: ['date'],
        translations: { en: 'Check-out Date', nl: 'Uitcheckdatum', de: 'Check-out-Datum' }
    },
    {
        id: 'room-type',
        fieldTypes: ['dropdown', 'text'],
        translations: { en: 'Room Type', nl: 'Kamertype', de: 'Zimmertyp' }
    },
    {
        id: 'special-requests',
        fieldTypes: ['multiline', 'text'],
        translations: { en: 'Special Requests', nl: 'Speciale verzoeken', de: 'Besondere Wünsche' }
    },
    // Files & media
    {
        id: 'upload-photo',
        fieldTypes: ['image', 'file'],
        translations: { en: 'Upload Photo', nl: 'Foto uploaden', de: 'Foto hochladen' }
    },
    {
        id: 'profile-photo',
        fieldTypes: ['image', 'file'],
        translations: { en: 'Profile Photo', nl: 'Profielfoto', de: 'Profilfoto' }
    },
    {
        id: 'upload-file',
        fieldTypes: ['file'],
        translations: { en: 'Upload File', nl: 'Bestand uploaden', de: 'Datei hochladen' }
    },
    {
        id: 'resume-cv',
        fieldTypes: ['file'],
        translations: { en: 'Resume/CV', nl: 'CV', de: 'Lebenslauf' }
    },
    {
        id: 'id-document',
        fieldTypes: ['file'],
        translations: { en: 'ID Document', nl: 'Identiteitsdocument', de: 'Ausweisdokument' }
    },
    // Consent / Undertaking
    {
        id: 'agree-terms',
        fieldTypes: ['toggle', 'radio', 'choice'],
        translations: { en: 'Agree to Terms', nl: 'Akkoord met voorwaarden', de: 'Bedingungen akzeptieren' }
    },
    {
        id: 'consent-marketing',
        fieldTypes: ['toggle', 'radio', 'choice'],
        translations: {
            en: 'Consent to Marketing',
            nl: 'Marketingtoestemming',
            de: 'Einwilligung in Marketing'
        }
    },
    {
        id: 'signature',
        fieldTypes: ['image', 'file', 'undertaking'],
        translations: { en: 'Signature', nl: 'Handtekening', de: 'Unterschrift' }
    },
    {
        id: 'undertaking-acknowledgment',
        fieldTypes: ['undertaking', 'toggle', 'choice'],
        translations: {
            en: 'Undertaking Acknowledgment',
            nl: 'Verklaring bevestiging',
            de: 'Verpflichtung bestätigen'
        }
    },
    {
        id: 'undertaking-reason',
        fieldTypes: ['undertaking', 'multiline', 'text'],
        translations: {
            en: 'Undertaking Reason',
            nl: 'Reden verklaring',
            de: 'Begründung der Verpflichtung'
        }
    },
    // Survey & feedback
    {
        id: 'satisfaction',
        fieldTypes: ['rating', 'choice', 'radio'],
        translations: { en: 'Satisfaction', nl: 'Tevredenheid', de: 'Zufriedenheit' }
    },
    {
        id: 'service-quality',
        fieldTypes: ['rating', 'choice', 'radio'],
        translations: { en: 'Service Quality', nl: 'Servicekwaliteit', de: 'Servicequalität' }
    },
    {
        id: 'food-quality',
        fieldTypes: ['rating', 'choice', 'radio'],
        translations: { en: 'Food Quality', nl: 'Voedselkwaliteit', de: 'Lebensmittelqualität' }
    },
    {
        id: 'venue-ambience',
        fieldTypes: ['rating', 'choice', 'radio'],
        translations: { en: 'Venue Ambience', nl: 'Sfeer van locatie', de: 'Veranstaltungsatmosphäre' }
    },
    {
        id: 'overall-rating',
        fieldTypes: ['rating', 'choice', 'radio'],
        translations: { en: 'Overall Rating', nl: 'Algemene beoordeling', de: 'Gesamtbewertung' }
    },
    {
        id: 'likelihood-to-recommend',
        fieldTypes: ['nps', 'rating', 'radio', 'choice'],
        translations: {
            en: 'Likelihood to Recommend',
            nl: 'Aanbevelingskans',
            de: 'Weiterempfehlungswahrscheinlichkeit'
        },
        aliases: { en: ['NPS'], nl: [], de: [] }
    },
    {
        id: 'recommendation-reason',
        fieldTypes: ['multiline', 'text'],
        translations: {
            en: 'Recommendation Reason',
            nl: 'Reden voor aanbeveling',
            de: 'Grund für Empfehlung'
        }
    },
    {
        id: 'comments',
        fieldTypes: ['multiline', 'text'],
        translations: { en: 'Comments', nl: 'Opmerkingen', de: 'Kommentare' },
        aliases: { en: ['Remarks', 'Notes'], nl: ['Notities'], de: ['Anmerkungen'] }
    },
    {
        id: 'feedback',
        fieldTypes: ['multiline', 'text'],
        translations: { en: 'Feedback', nl: 'Feedback', de: 'Feedback' }
    },
    // Preferences & misc
    {
        id: 'interests',
        fieldTypes: ['multi-select', 'checkbox-group', 'dropdown'],
        translations: { en: 'Interests', nl: 'Interesses', de: 'Interessen' }
    },
    {
        id: 'languages-spoken',
        fieldTypes: ['multi-select', 'checkbox-group', 'dropdown'],
        translations: { en: 'Languages Spoken', nl: 'Gesproken talen', de: 'Gesprochene Sprachen' }
    },
    {
        id: 't-shirt-size',
        fieldTypes: ['dropdown', 'choice', 'radio'],
        translations: { en: 'T-shirt Size', nl: 'T-shirtmaat', de: 'T-Shirt-Größe' }
    },
    {
        id: 'newsletter-subscription',
        fieldTypes: ['toggle', 'radio', 'choice'],
        translations: { en: 'Newsletter Subscription', nl: 'Nieuwsbriefabonnement', de: 'Newsletter-Abonnement' }
    },
    // Company selection (custom)
    {
        id: 'select-company',
        fieldTypes: ['companyDropdown', 'dropdown'],
        translations: { en: 'Select Company', nl: 'Selecteer bedrijf', de: 'Unternehmen auswählen' }
    },
    // Ranking
    {
        id: 'rank-priorities',
        fieldTypes: ['ranking'],
        translations: { en: 'Rank Priorities', nl: 'Prioriteiten rangschikken', de: 'Prioritäten rangieren' }
    },
    {
        id: 'rank-choices',
        fieldTypes: ['ranking'],
        translations: { en: 'Rank Choices', nl: 'Keuzes rangschikken', de: 'Optionen rangieren' }
    }
];
export function getFieldLabelSuggestions({ lang = 'en', query, fieldType, limit = 20 }) {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery)
        return [];
    // Filter by field type if provided
    const pool = FIELD_LABEL_SUGGESTIONS.filter((s) => {
        if (!fieldType)
            return true;
        if (s.fieldTypes[0] === 'any')
            return true;
        return s.fieldTypes.includes(fieldType);
    });
    const scored = [];
    for (const s of pool) {
        const label = s.translations[lang] || s.translations['en'] || '';
        const labelLc = label.toLowerCase();
        let score = -1;
        if (labelLc.startsWith(normalizedQuery))
            score = 100;
        else if (labelLc.includes(normalizedQuery))
            score = 50;
        const aliases = (s.aliases?.[lang] || []).map((a) => a.toLowerCase());
        const keywords = (s.keywords?.[lang] || []).map((k) => k.toLowerCase());
        if (score < 0) {
            if (aliases.some((a) => a.startsWith(normalizedQuery)))
                score = 40;
            else if (aliases.some((a) => a.includes(normalizedQuery)))
                score = 30;
            else if (keywords.some((k) => k.startsWith(normalizedQuery)))
                score = 20;
            else if (keywords.some((k) => k.includes(normalizedQuery)))
                score = 10;
        }
        if (score >= 0)
            scored.push({ id: s.id, label, score });
    }
    return scored
        .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
        .slice(0, limit)
        .map(({ id, label }) => ({ id, label }));
}
